import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    QuestionMarkCircleIcon,
    ChevronDownIcon,
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline'
import { faqAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'

const FAQs = () => {
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')
    const [openIndex, setOpenIndex] = useState(0)

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['faqs', searchQuery, selectedCategory],
        queryFn: () => faqAPI.getFAQs({
            search: searchQuery,
            category: selectedCategory
        }),
    })

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? -1 : index)
    }

    const handleFeedback = async (faqId, isHelpful) => {
        try {
            if (isHelpful) {
                await faqAPI.markHelpful(faqId)
            } else {
                await faqAPI.markNotHelpful(faqId)
            }
        } catch (error) {
            console.error('Failed to submit feedback:', error)
        }
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load FAQs" onRetry={refetch} />
            </div>
        )
    }

    const faqs = data?.data?.faqs || []
    const categories = [
        { value: '', label: 'All Categories' },
        { value: 'general', label: 'General' },
        { value: 'admission', label: 'Admission' },
        { value: 'fees', label: 'Fees' },
        { value: 'courses', label: 'Courses' },
        { value: 'university', label: 'University' },
        { value: 'eligibility', label: 'Eligibility' },
        { value: 'career', label: 'Career' },
        { value: 'placement', label: 'Placement' },
    ]

    return (
        <>
            <Helmet>
                <title>Frequently Asked Questions | Margdarshak</title>
                <meta name="description" content="Find answers to common questions about online education, admissions, fees, and career guidance. Get help with your education journey." />
                <meta name="keywords" content="FAQ, frequently asked questions, online education, admission help, career guidance" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-20">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-primary-600 via-secondary-600 to-primary-800 text-white py-16 relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-accent-100/20 backdrop-blur-sm rounded-full mb-8">
                                <QuestionMarkCircleIcon className="w-10 h-10 text-accent-300" />
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                                Frequently Asked <span className="text-accent-400">Questions</span>
                            </h1>
                            <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto text-white drop-shadow-md">
                                Find answers to common questions about our programs, admissions, and services
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Search and Filter Section */}
                <section className="py-8 bg-white border-b border-gray-200">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search FAQs..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    />
                                </div>
                            </div>

                            {/* Category Filter */}
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px]"
                            >
                                {categories.map((category) => (
                                    <option key={category.value} value={category.value}>
                                        {category.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </section>

                {/* FAQs Section */}
                <section className="py-12">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <LoadingSpinner size="lg" />
                            </div>
                        ) : (
                            <>
                                {faqs.length > 0 ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                        className="space-y-4"
                                    >
                                        {faqs.map((faq, index) => (
                                            <motion.div
                                                key={faq.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                                className="card overflow-hidden"
                                            >
                                                <button
                                                    onClick={() => toggleFAQ(index)}
                                                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                                                            {faq.question}
                                                        </h3>
                                                        {faq.category && (
                                                            <span className="inline-block mt-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full">
                                                                {faq.category}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <motion.div
                                                        animate={{ rotate: openIndex === index ? 180 : 0 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="flex-shrink-0"
                                                    >
                                                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                                                    </motion.div>
                                                </button>

                                                <AnimatePresence>
                                                    {openIndex === index && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="px-6 pb-4">
                                                                <div className="w-full h-px bg-gray-200 mb-4"></div>
                                                                <p className="text-gray-600 leading-relaxed mb-4">
                                                                    {faq.answer}
                                                                </p>

                                                                {/* Feedback Section */}
                                                                {/* <div className="flex items-center space-x-4 pt-4 border-t border-gray-100">
                                                                    <span className="text-sm text-gray-500">Was this helpful?</span>
                                                                    <div className="flex items-center space-x-2">
                                                                        <button
                                                                            onClick={() => handleFeedback(faq.id, true)}
                                                                            className="flex items-center space-x-1 text-green-600 hover:text-green-700 text-sm font-medium"
                                                                        >
                                                                            <CheckCircleIcon className="w-4 h-4" />
                                                                            <span>Yes</span>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleFeedback(faq.id, false)}
                                                                            className="flex items-center space-x-1 text-red-600 hover:text-red-700 text-sm font-medium"
                                                                        >
                                                                            <XCircleIcon className="w-4 h-4" />
                                                                            <span>No</span>
                                                                        </button>
                                                                    </div>
                                                                </div> */}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                        className="text-center py-12"
                                    >
                                        <QuestionMarkCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No FAQs Found</h3>
                                        <p className="text-gray-600">
                                            {searchQuery || selectedCategory
                                                ? 'Try adjusting your search or filter criteria.'
                                                : 'No FAQs are available at the moment.'
                                            }
                                        </p>
                                    </motion.div>
                                )}
                            </>
                        )}
                    </div>
                </section>

                {/* Contact Section */}
                <section className="py-16 bg-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Still have questions?
                            </h2>
                            <p className="text-xl text-gray-600 mb-8">
                                Can't find the answer you're looking for? Our support team is here to help.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a
                                    href="/contact"
                                    className="btn-primary btn-lg"
                                >
                                    Contact Support
                                </a>
                                <button className="btn-outline btn-lg">
                                    Get Free Consultation
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default FAQs
